import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface ExpiryCountdownProps {
  expiresAt: string;
  className?: string;
  /** Called once when the countdown reaches zero (e.g. to refetch and refresh UI) */
  onExpired?: () => void;
}

export function ExpiryCountdown({ expiresAt, className = '', onExpired }: ExpiryCountdownProps) {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState<{ minutes: number; seconds: number } | null>(null);
  const [expired, setExpired] = useState(false);
  const onExpiredCalledRef = useRef(false);

  useEffect(() => {
    const update = () => {
      const end = new Date(expiresAt).getTime();
      const now = Date.now();
      const diff = Math.max(0, Math.floor((end - now) / 1000));
      if (diff <= 0) {
        setExpired(true);
        setRemaining({ minutes: 0, seconds: 0 });
        return;
      }
      setRemaining({
        minutes: Math.floor(diff / 60),
        seconds: diff % 60,
      });
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  useEffect(() => {
    if (expired && onExpired && !onExpiredCalledRef.current) {
      onExpiredCalledRef.current = true;
      onExpired();
    }
  }, [expired, onExpired]);

  if (remaining === null) return null;

  if (expired) {
    return (
      <span className={className}>
        {t('temporaryNumbers.countdown.expired')}
      </span>
    );
  }

  const m = String(remaining.minutes).padStart(2, '0');
  const s = String(remaining.seconds).padStart(2, '0');

  return (
    <span className={className}>
      {t('temporaryNumbers.countdown.remaining')}:{' '}
      <span className="font-mono font-semibold text-brand-accent tabular-nums">
        {m}:{s}
      </span>
      <span className="text-neutral-medium text-sm ml-1">
        ({t('temporaryNumbers.countdown.minutes')})
      </span>
    </span>
  );
}
